// The meeting recorder, one per page, outside React: navigating anywhere in
// Corvus, a narrow window switching to the phone screens, or a sign-out
// never touches a recording (RecorderProvider only binds screens to it).
//
// A recording is a series of takes; each take is one MediaRecorder run with
// its own client_ref. Audio is captured from the tap; every piece is stored
// on the device first, then sent (recorderUpload.ts). Stop waits for the
// recorder's last piece, sends the rest and finishes. When the microphone
// goes (unplugged, the system took it) the take finishes and a new one
// carries on into the same meeting by itself where the browser allows it,
// else with one tap. When the page dies, the next page of Corvus on this
// browser finds the take: it sends what is left and asks "Carry on?", and
// finishes it on its own at the meeting's end + 15 min (30 min after the
// last audio for a meeting not on the calendar).

import { formatTime } from "@/lib/format";
import { iso, lockName, type CarryOnOffer, type StartOptions } from "./recorderCore";
import { RecorderTakes } from "./recorderTakes";
import { QUIET_LEVEL, TIMESLICE_MS, addAudio, mayAutoCarry, partRanges } from "./recorderModel";
import type { TakeRecord } from "./recorderStore";
import { newRuntime, type Runtime, type StreamLike } from "./recorderTypes";

export type { CarryOnOffer, KeptAudio, RecorderPhase, RecorderSnapshot, StartOptions } from "./recorderCore";

export class RecorderEngine extends RecorderTakes {
  /** Stop was asked for while the microphone was still being asked for: drop the stream when it comes. */
  private stopAfterStart = false;
  /** When the microphone last came back by itself (a flaky device is asked about, not chased). */
  private autoCarries: number[] = [];
  private persistAsked = false;
  /** Counts "stop and upload everything" calls: a carry-on the microphone started before one never goes on after it. */
  private stopEpoch = 0;

  /**
   * A signed-in screen for this workspace and person (`owner`: recorderModel.ownerKey;
   * `family`: a child): take over what a closed page of theirs left, and send it. Another
   * person's takes on this browser are left alone (and not sent with this sign-in).
   */
  async bind(customerId: string, who: { owner: string; family: boolean } = { owner: "", family: false }): Promise<void> {
    this.signInAs(customerId, who);
    this.bound = true;
    await this.scan();
    await this.adoptStored();
    this.rescan ??= this.deps.clock.setInterval(() => void this.adoptStored(), 60_000);
    this.askAgain();
    for (const rt of this.mine()) void this.uploader.pump(rt);
  }

  /** The screens went away (a sign-out): a recording carries on behind them. */
  unbind(): void {
    this.bound = false;
    this.adopted = false;
    this.changed();
  }

  /** Record. Call straight from the tap: the microphone is asked for before anything else. */
  async start(o: StartOptions): Promise<void> {
    if (this.live || this.requesting) return;
    if (!this.customerId) {
      this.failure = "Sign in to record.";
      this.changed();
      return;
    }
    if (!this.deps.media.supported()) {
      this.failure = "This browser can't record audio.";
      this.changed();
      return;
    }
    this.requesting = true;
    this.failure = null;
    this.notice = null;
    this.clearAfter();
    this.changed();
    const stream = await this.askMic(false, !!o.callSound);
    if (!stream) return;
    // Recording again on the meeting a cut-short take belongs to is carrying it on.
    const offer = this.myOffer();
    const held = offer && this.rts.get(offer.takeId);
    if (offer && held && o.meetingId && held.rec.meetingId === o.meetingId) return this.continueFrom(offer, stream);
    if (offer?.finishAt === null) this.offer = null;
    // An earlier take of this page on the same meeting not filed yet goes first: the segments keep the order they were recorded in.
    this.beginTake(stream, { ...o, followsTake: o.meetingId ? this.unfiledTakeOf(o.meetingId) : null });
  }

  /** The microphone (`callSound`: mixed with the call's shared sound), from the tap; null when refused or a Stop / another take came first. */
  private async askMic(quiet = false, callSound = false): Promise<StreamLike | null> {
    let stream: StreamLike;
    try {
      stream = await this.deps.media.getUserMedia(callSound ? { callSound: true } : undefined);
    } catch (e) {
      this.requesting = false;
      this.stopAfterStart = false;
      this.refused(e as Error, quiet, callSound);
      return null;
    }
    if (this.live || this.stopAfterStart) {
      stream.getTracks().forEach((t) => t.stop());
      this.requesting = false;
      this.stopAfterStart = false;
      this.changed();
      return null;
    }
    return stream;
  }

  /** Stop: wait for the recorder's last piece, then send the rest and finish. */
  async stop(): Promise<void> {
    const rt = this.live;
    if (!rt) {
      if (this.requesting) this.stopAfterStart = true;
      return;
    }
    if (rt.stopping) return;
    rt.stopping = true;
    this.changed();
    const recorder = rt.recorder;
    if (recorder && recorder.state !== "inactive") {
      await new Promise<void>((resolve) => {
        const t = this.deps.clock.setTimeout(() => {
          rt.stopWaiter = null;
          resolve();
        }, 4_000);
        rt.stopWaiter = () => {
          this.deps.clock.clearTimeout(t);
          resolve();
        };
        try {
          recorder.stop();
        } catch {
          rt.stopWaiter = null;
          this.deps.clock.clearTimeout(t);
          resolve();
        }
      });
    }
    rt.finalIn = true;
    this.endCapture(rt, false);
    rt.stopping = false;
    rt.rec.status = "closing";
    this.save(rt);
    this.changed();
    void this.uploader.pump(rt);
  }

  /** "Carry on": a new take in the same meeting, from the tap; `callSound` as the screen's choice says (else as recorded). */
  async carryOn(how?: { callSound?: boolean }): Promise<void> {
    const o = this.myOffer();
    if (!o || this.live || this.requesting) return;
    this.requesting = true;
    this.failure = null;
    this.changed();
    const stream = await this.askMic(false, how?.callSound ?? !!o.callSound);
    if (stream) this.continueFrom(this.myOffer() ?? o, stream);
  }

  /** "Finish now": what a cut-short take holds goes for notes without waiting. */
  finishNow(): void {
    const o = this.myOffer();
    if (!o) return;
    const rt = this.rts.get(o.takeId);
    this.offer = null;
    if (rt && rt.rec.status === "held") this.close(rt);
    this.changed();
  }

  /**
   * "Stop and upload everything now" (the Mac app before a quit, a sign-out
   * that asked): stop the live take, drop a microphone still being asked
   * for, and finish every take of this account held for "Carry on".
   */
  async stopAndUpload(): Promise<void> {
    this.stopEpoch++;
    if (this.requesting && !this.live) this.stopAfterStart = true;
    // Nothing carries on after this: no "Carry on" left for a take that has gone for notes.
    if (this.myOffer()?.finishAt === null) this.offer = null;
    for (const rt of this.mine()) {
      if (rt.rec.status !== "held") continue;
      if (this.offer?.takeId === rt.rec.id) this.offer = null;
      this.close(rt);
    }
    this.changed();
    await this.stop();
  }

  dismissOffer(): void {
    if (this.myOffer()?.finishAt === null) this.offer = null;
    this.changed();
  }

  /** Back to "Start recording" after a finished or failed one. */
  reset(): void {
    this.clearAfter();
    this.failure = null;
    this.notice = null;
    this.changed();
  }

  /** The page is hidden (`leaving`: pagehide, it may not come back): ask the recorder for what it holds now. */
  pageHidden(leaving = false): void {
    this.exiting = leaving;
    const rt = this.live;
    if (rt?.recorder?.state === "recording") {
      try {
        rt.recorder.requestData();
      } catch { /* an engine without requestData: the next timeslice brings it */ }
    }
    for (const r of this.rts.values()) if (r.rec.ownerTab === this.deps.tabId) this.save(r);
  }

  /** Back on screen: keep the screen awake again, and check the microphone is still live. */
  pageShown(): void {
    this.exiting = false;
    const rt = this.live;
    if (!rt) return;
    rt.releaseWake?.();
    rt.releaseWake = this.deps.media.wakeLock();
    const tracks = rt.stream?.getTracks() ?? [];
    if (tracks.some((t) => t.readyState === "ended")) return this.lost(rt, "microphone_lost");
    if (tracks.some((t) => t.muted)) {
      this.deps.clock.setTimeout(() => {
        if (this.live === rt && (rt.stream?.getTracks() ?? []).some((t) => t.muted)) this.lost(rt, "microphone_lost");
      }, 3_000);
    }
  }

  // ─── takes ───

  /** A new take on `stream` (null when one already records: never two microphones). */
  private beginTake(
    stream: StreamLike,
    o: StartOptions & { followsTake: string | null; chainStartedAt?: number },
  ): Runtime | null {
    const { media, clock } = this.deps;
    if (this.live) {
      // Never two microphones: what is already recording goes on.
      stream.getTracks().forEach((t) => t.stop());
      this.requesting = false;
      this.changed();
      return null;
    }
    if (!this.persistAsked) {
      this.persistAsked = true;
      void this.ensureStore().then((s) => s.persist?.()).catch(() => undefined);
    }
    const mime = media.pickMime();
    const recorder = media.createRecorder(stream, mime);
    const now = clock.now();
    const rec: TakeRecord = {
      id: this.deps.uuid(), customerId: this.customerId ?? "", meetingId: o.meetingId, title: o.title,
      meetingTitle: o.meetingTitle ?? null, meetingEndAt: o.meetingEndAt ?? null,
      mime: recorder.mimeType || mime || "audio/webm", continueRecording: !!o.continueRecording,
      upload: null, formerUploads: [], followsTake: o.followsTake, segment: 1, startedAt: now, lastAudioAt: now,
      audioSec: 0, nextSeq: 0, sentBelow: 0, status: "capturing", cutShortAt: null, cutReason: null, finishAt: null,
      interruptions: [], ownerTab: this.deps.tabId, aliveAt: now, owner: this.owner,
      ...(o.chainStartedAt !== undefined ? { chainStartedAt: o.chainStartedAt } : {}),
      ...(stream.callSound ? { callSound: true } : {}),
    };
    const rt = newRuntime(rec, true);
    rt.stream = stream;
    rt.recorder = recorder;
    recorder.ondataavailable = (e) => this.onPiece(rt, e.data);
    recorder.onstop = () => this.onRecorderStop(rt);
    recorder.onerror = () => {
      if (recorder.state === "inactive") this.lost(rt, "microphone_lost");
    };
    this.watchCallSound(rt, stream);
    for (const track of stream.getTracks()) {
      track.addEventListener("ended", () => this.lost(rt, "microphone_lost"));
      track.addEventListener("mute", () => {
        rt.mutedSince ??= clock.now();
        this.changed();
      });
      track.addEventListener("unmute", () => this.unmuted(rt));
    }
    recorder.start(TIMESLICE_MS);
    this.live = rt;
    this.rts.set(rec.id, rt);
    this.requesting = false;
    this.lastLoudAt = now;
    rt.stopMeter = media.meter(stream, (v) => this.onLevel(v));
    rt.releaseWake = media.wakeLock();
    this.tick ??= clock.setInterval(() => this.onTick(), 1_000);
    this.save(rt);
    void this.deps.locks.hold(lockName(rec.id)).then((release) => {
      rt.releaseLock = release;
      if (rt.rec.status === "done") release?.();
    });
    this.changed();
    void this.uploader.pump(rt);
    return rt;
  }

  /**
   * Carry `o`'s take on: a new take in the same meeting, filed after it. The take it
   * carries on finishes only once the new one is filed (Uploader: finishAfter), so the
   * meeting is never sent for notes between the two.
   */
  private continueFrom(o: CarryOnOffer, stream: StreamLike): void {
    const prev = this.rts.get(o.takeId);
    const now = this.deps.clock.now();
    if (prev) {
      const last = prev.rec.interruptions[prev.rec.interruptions.length - 1];
      if (last && !last.resumed_at) last.resumed_at = iso(now);
      else prev.rec.interruptions.push({ at: iso(o.at), resumed_at: iso(now), reason: o.reason });
    }
    this.offer = null;
    this.clearAfter();
    const r = prev?.rec;
    const next = this.beginTake(stream, {
      meetingId: r?.meetingId ?? o.meetingId, title: r?.title ?? o.title, meetingTitle: r?.meetingTitle ?? null,
      meetingEndAt: r?.meetingEndAt ?? null, continueRecording: true, followsTake: prev ? o.takeId : null,
      chainStartedAt: r ? (r.chainStartedAt ?? r.startedAt) : undefined,
    });
    if (prev) {
      if (next) prev.rec.finishAfter = next.rec.id;
      if (prev.rec.status === "held") this.close(prev);
      else this.save(prev);
    }
  }

  private onPiece(rt: Runtime, blob: Blob | null | undefined): void {
    try {
      this.takePiece(rt, blob);
    } finally {
      this.pieceIn(rt);
    }
  }

  private takePiece(rt: Runtime, blob: Blob | null | undefined): void {
    if (!blob || blob.size === 0) return;
    const r = rt.rec;
    if (r.status === "done" || r.status === "kept") {
      // The recorder's last piece came after the take finished (a throttled tab): never stored under a take that is gone.
      this.diag(rt, "late_piece", { reason: "after_finish" });
      return;
    }
    const now = this.deps.clock.now();
    r.audioSec = addAudio(r.audioSec, r.lastAudioAt, now);
    r.lastAudioAt = now;
    r.aliveAt = now;
    let last: [number, Blob] | null = null;
    for (const [a, b] of partRanges(blob.size)) {
      const seq = r.nextSeq++;
      const part = blob.slice(a, b, blob.type);
      rt.blobs.set(seq, part);
      rt.stored.set(seq, this.storePart(rt, seq, part));
      last = [seq, part];
    }
    this.save(rt);
    this.changed();
    if (this.exiting && last) this.uploader.sendOnExit(rt, last[0], last[1]);
    void this.uploader.pump(rt);
  }

  private async storePart(rt: Runtime, seq: number, data: Blob): Promise<void> {
    try {
      const store = await this.ensureStore();
      await store.putPart({ takeId: rt.rec.id, seq, data });
    } catch {
      // Only in this page until it reaches the server (decisions D1: storage "memory" while so).
      if (rt.rec.status === "done") return;
      rt.unstored.add(seq);
      this.changed();
    }
  }

  private onRecorderStop(rt: Runtime): void {
    rt.finalIn = true;
    if (rt.stopWaiter) {
      const w = rt.stopWaiter;
      rt.stopWaiter = null;
      w();
    } else if (rt.rec.status === "capturing" && !rt.stopping) {
      this.lost(rt, "microphone_lost");
    }
    void this.uploader.pump(rt);
  }

  /**
   * The microphone went without Stop: finish this take, offer (or make) the carry-on. It
   * stopped recording when the system muted it, if it did (else now). While a carry-on by
   * itself may still come, the take is not finished (one set of notes).
   */
  private lost(rt: Runtime, reason: string): void {
    if (rt.rec.status !== "capturing" || rt.stopping) return;
    const now = this.deps.clock.now();
    const at = rt.mutedSince ?? now;
    rt.rec.interruptions.push({ at: iso(at), resumed_at: null, reason });
    this.endCapture(rt, true);
    rt.rec.status = "closing";
    this.save(rt);
    this.offer = this.offerFor(rt, at, reason, null);
    // A call recording never carries on by itself: without a tap the call cannot be shared again,
    // and the microphone alone would record the room, not the call.
    rt.carryPending = this.owns(rt.rec) && !rt.rec.callSound && !this.live && !this.requesting && mayAutoCarry(this.autoCarries, now);
    this.changed();
    void this.autoCarryOn(rt, at);
    void this.uploader.pump(rt);
  }

  /**
   * Where the browser lets the microphone open again without a tap, carry on
   * at once: one request at a time (a tap on Carry on meanwhile waits for
   * it), and at most AUTO_CARRY_MAX times in AUTO_CARRY_WINDOW_MS; a device
   * that keeps dropping is then asked about, not chased.
   */
  private async autoCarryOn(prev: Runtime, at: number): Promise<void> {
    // A permission check or a microphone that never answers does not hold the take back for long.
    const giveUp = this.deps.clock.setTimeout(() => this.carrySettled(prev), 60_000);
    try {
      // (Set by lost(): this person's take, nothing else asking for the microphone, not too often.)
      if (!prev.carryPending) return;
      const epoch = this.stopEpoch;
      if (!(await this.deps.media.micGranted().catch(() => false))) return;
      // Checked again after the wait: a tap on Carry on (or Record) meanwhile has the microphone request,
      // and a "stop and upload everything" meanwhile means nothing carries on.
      if (this.live || this.requesting || this.offer?.takeId !== prev.rec.id || epoch !== this.stopEpoch) return;
      this.requesting = true;
      this.changed();
      const stream = await this.askMic(true);
      if (!stream) return;
      if (this.offer?.takeId !== prev.rec.id) {
        stream.getTracks().forEach((t) => t.stop());
        this.requesting = false;
        this.changed();
        return;
      }
      this.autoCarries.push(this.deps.clock.now());
      this.continueFrom(this.offer, stream);
      this.notice = `The microphone stopped at ${formatTime(new Date(at))} and came back; the recording carries on.`;
      this.changed();
    } finally {
      this.deps.clock.clearTimeout(giveUp);
      this.carrySettled(prev);
    }
  }

  /** Carried on by itself, or not: the take may finish (after its carry-on is filed, if there is one). */
  private carrySettled(prev: Runtime): void {
    if (!prev.carryPending) return;
    prev.carryPending = false;
    void this.uploader.pump(prev);
  }

  /** Stop the microphone and what hangs off it. `askRecorder`: it may still hold a last piece. */
  private endCapture(rt: Runtime, askRecorder: boolean): void {
    rt.stopMeter?.();
    rt.stopMeter = null;
    rt.releaseWake?.();
    rt.releaseWake = null;
    const recorder = rt.recorder;
    if (askRecorder && recorder && recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch {
        rt.finalIn = true;
      }
      this.deps.clock.setTimeout(() => {
        if (!rt.finalIn) {
          rt.finalIn = true;
          void this.uploader.pump(rt);
        }
      }, 4_000);
    } else {
      rt.finalIn = true;
    }
    rt.stream?.getTracks().forEach((t) => t.stop());
    rt.stream = null;
    if (this.live === rt) this.live = null;
    if (!this.live && this.tick !== null) {
      this.deps.clock.clearInterval(this.tick);
      this.tick = null;
      this.level = 0;
    }
  }

  private onLevel(v: number): void {
    this.level = v;
    if (v >= QUIET_LEVEL) this.lastLoudAt = this.deps.clock.now();
    this.changed();
  }

  private onTick(): void {
    const rt = this.live;
    const now = this.deps.clock.now();
    if (rt && now - rt.rec.aliveAt > 5_000) {
      rt.rec.aliveAt = now;
      this.save(rt);
    }
    // A recorder that ignores the timeslice (older Safari hands over one piece at stop): ask for it.
    if (rt?.recorder?.state === "recording" && now - rt.rec.lastAudioAt > TIMESLICE_MS * 2.5) {
      try {
        rt.recorder.requestData();
      } catch { /* none to give */ }
    }
    this.changed();
  }
}
